import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import Button from '../ui/Button';
import { ErrorNotice } from '../ui/States';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import { fmt } from '../../lib/format';
import { COUNT_LABEL, EventCounts } from '../../lib/events';
import { errorMessage, inputClass } from '../../lib/programs';

const blank = (c: EventCounts | null) =>
  Object.fromEntries(COUNT_LABEL.map(([k]) => [k, c?.[k] == null ? '' : String(c[k])])) as Record<
    keyof EventCounts,
    string
  >;

/** Attendance as numbers only (trainees, faculty, other, total, registered); no names. */
export default function CountsEditor({
  eventId,
  counts,
  canEdit,
}: {
  eventId: string;
  counts: EventCounts | null;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blank(counts));
  const save = useMutation(
    async () => {
      const body = Object.fromEntries(COUNT_LABEL.map(([k]) => [k, form[k] === '' ? null : Number(form[k])]));
      return api.patch(`/events/${eventId}`, { counts: body });
    },
    {
      onSuccess: () => {
        setEditing(false);
        queryClient.invalidateQueries('event');
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries('event-content');
        queryClient.invalidateQueries('event-insights');
      },
    },
  );
  if (!editing) {
    return (
      <div className="text-table">
        {counts ? (
          <ul className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {COUNT_LABEL.map(([k, label]) => (
              <li key={k}>
                <div className="font-semibold text-[26px] leading-none">{counts[k] == null ? '—' : fmt(counts[k])}</div>
                <div className="text-small text-ink-2">{label}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-ink-2">No numbers recorded yet.</p>
        )}
        {canEdit ? (
          <Button
            size="sm"
            className="mt-3"
            onClick={() => {
              setForm(blank(counts));
              setEditing(true);
            }}
          >
            {counts ? 'Change the numbers' : 'Record the numbers'}
          </Button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="text-small">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {COUNT_LABEL.map(([k, label]) => (
          <label key={k}>
            {label}
            <input
              className={`${inputClass} block w-full`}
              inputMode="numeric"
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value.replace(/[^0-9]/g, '') })}
            />
          </label>
        ))}
      </div>
      <p className="text-ink-2 mt-2">Leave the total empty to add up trainees, faculty and other.</p>
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
          {save.isLoading ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
      {save.error ? (
        <div className="mt-2">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the numbers.')}</ErrorNotice>
        </div>
      ) : null}
    </div>
  );
}

/** The "who came" block of a counts-only event: a panel once numbers exist, a one-line prompt for editors before. */
export function AttendanceNumbers({
  eventId,
  counts,
  canEdit,
  past,
}: {
  eventId: string;
  counts: EventCounts | null;
  canEdit: boolean;
  past: boolean;
}) {
  if (counts)
    return (
      <Panel className="lg:col-span-2">
        <PanelHeader title={past ? 'Who came' : 'Who is expected'} note="numbers only, no names" />
        <PanelBody>
          <CountsEditor eventId={eventId} counts={counts} canEdit={canEdit} />
        </PanelBody>
      </Panel>
    );
  return canEdit ? (
    <div className="lg:col-span-3">
      <CountsEditor eventId={eventId} counts={null} canEdit />
    </div>
  ) : null;
}
